import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from '@ignitesales/neuro-application/src/neuro-ui/lib';
import { NeuroMessage } from '@ignitesales/neuro-application/src/neuro-guide';
import { SearchResults, CompanyEditForm, VIEWS } from '../components/index.mjs';
import { STATE_DROPDOWN_OPTIONS } from './constants.mjs';

const LookupFlow = ({ classes, isOpen, onClose, results = {}, onContactSearch, type, value = {}, valueChanged, accountIdParam }) => {
  const [view, setView] = useState({
    current: type,
    history: [],
  });

  const [selections, setSelections] = useState({
    company: { index: -1 },
    contact: { index: -1 },
  });

  useEffect(() => {
    // do any resets when modal opens
    if (isOpen) {
      setView({
        current: type,
        history: []
      });
    }
  }, [isOpen]);

  const contactTransform = (data) => ({
    ...data,
    contactName: `${data.firstName} ${data.lastName}`,
  });

  const saveCompany = (data, index, options = {}) => {
    const { edit, reset } = options;

    setSelections({
      company: {
        index: reset ? -1 : index,
      },
      contact: {
        index: reset ? -1 : selections.contact.index
      }
    });

    const mergedData = reset ? {
      ...data,
      firstName: undefined,
      lastName: undefined,
      email: undefined,
      contactPhone: undefined,
      isAdd: true,
      isEdit: false,
    } : {
      ...value,
      ...data,
      ...(!edit && { isAdd: false }),
      isEdit: edit,
    };
    valueChanged(mergedData);
  };

  const saveContact = (data, index, options = {}) => {
    const { transform, reset, edit } = options;
    setSelections({
      ...selections,
      contact: {
        index: reset ? -1 : index
      }
    });
    const formattedData = transform ? contactTransform(data) : data;

    const mergedData = {
      ...value,
      ...formattedData,
      ...(reset && { isAdd: true }),
      isEdit: edit,
      ...(accountIdParam && { accountId: accountIdParam }),
      ...results.account
    };
    valueChanged(mergedData);
  };

  const navigate = nextView => {
    const history = [...view.history];
    history.push(view.current);
    setView({
      current: nextView,
      history
    });
  };

  const onAddItem = nextView => () => {
    switch (nextView) {
      case VIEWS.ADD_COMPANY:
        valueChanged({});
        break;
      case VIEWS.ADD_CONTACT:
        valueChanged({
          ...value, 
          firstName: undefined,
          lastName: undefined,
          email: undefined,
          contactPhone: undefined,
          contactId: undefined,
          ...results.account
        });
        break;
    }
    navigate(nextView);
  };

  const onContinue = nextView => (data, index) => {
    //probably want to enable some sort of loading state here somewhere...
    switch (view.current) {
      case VIEWS.COMPANY_RESULTS:
        saveCompany(data, index);
        onContactSearch(data);
        break;
      case VIEWS.ADD_COMPANY:
        saveCompany(data, index, { reset: true });
        break;
      case VIEWS.EDIT_COMPANY:
        saveCompany(data, index, { edit: true });
        break;
      case VIEWS.CONTACT_RESULTS:
        saveContact(data, index);
        break;
      case VIEWS.ADD_CONTACT:
        saveContact(data, index, { transform: true, reset: true });
        break;
      case VIEWS.EDIT_CONTACT:
        saveContact(data, index, { transform: true, edit: true });
        break;
    }
    return nextView ? navigate(nextView) : onClose();
  };

  const onBack = () => {
    const history = [...view.history];
    const current = history.pop();
    setView({
      current,
      history
    });
  };

  const renderResults = (addItemView, res, selectedResult, continueView) => {
    const messages = {
      _add: <NeuroMessage id={`lookupFlow.${view.current}.addButton`} />,
      _continue: <NeuroMessage id="lookupFlow.continue" />,
      _resultsFound: <NeuroMessage id="lookupFlow.resultsFound" />,
      _title: <NeuroMessage id={`lookupFlow.${view.current}.title`} />,
    };
    const resultTransform = result => {
      return view.current === VIEWS.COMPANY_RESULTS ? 
        {
          name: result.companyName,
          phone: result.companyPhone,
          address: result.streetAddress,
          city: result.city,
          state: result.state,
        } :
        {
          name: result.contactName,
          phone: result.contactPhone,
          email: result.email,
        }
    };
    
    return (
      <SearchResults
        className={classes}
        onAddItem={onAddItem(addItemView)}
        onContinue={onContinue(continueView)}
        results={res}
        messages={messages}
        selectedResult={selectedResult}
        applyTransform={resultTransform}
      />);
  };

  const renderAddCompany = (continueView) => {
    const fields = [
      { name: 'companyName', initialValue: value.name || '', required: true, maxLength: '35'},
      { name: 'streetAddress', initialValue: value.streetAddress || '', required: true },
      { name: 'city', initialValue: value.city || '', required: true },
      { name: 'state', type: 'dropdown', options: STATE_DROPDOWN_OPTIONS, initialValue: value.state || '', required: true },
      { name: 'zipCode', initialValue: value.zipCode || '', required: true, matchPattern: /^[0-9]{5}$/ },
      { name: 'companyPhone', initialValue: value.companyPhone || '', required: true, isPhoneNumber: true }
    ];
    return (<CompanyEditForm formId='addCompany' fields={fields} view={view} onContinue={onContinue(continueView)} />);
  };

  const renderEditCompany = (continueView) => {
    const fields = [
      { name: 'companyName', initialValue: value.companyName || '', maxLength: '35', required: true  },
      { name: 'streetAddress', initialValue: value.streetAddress || '', required: true  },
      { name: 'city', initialValue: value.city || '', required: true  },
      { name: 'state', type: 'dropdown', options: STATE_DROPDOWN_OPTIONS, initialValue: value.state || '', required: true  },
      { name: 'zipCode', initialValue: value.zipCode || '', matchPattern: /^[0-9]{5}$/, required: true  },
      { name: 'companyPhone', initialValue: value.companyPhone || '', isPhoneNumber: true, required: true  }
    ];
    return (<CompanyEditForm formId='editCompany' fields={fields} view={view} onContinue={onContinue(continueView)} />);
  };

  const renderAddContact = () => {

    const fields = [
      { name: 'firstName', initialValue: value.firstName || '', required: true },
      { name: 'lastName', initialValue: value.lastName || '', required: true },
      { name: 'contactPhone', initialValue: value.contactPhone || '', required: true, isPhoneNumber: true },
      { name: 'email', initialValue: value.email || '', required: true, isEmail: true, type: 'email' },
    ];
    return (<CompanyEditForm formId='addContact' fields={fields} view={view} onContinue={onContinue()} />);
  };
  const renderEditContact = () => {

    const fields = [
      { name: 'firstName', initialValue: value.firstName || '', required: true  },
      { name: 'lastName', initialValue: value.lastName || '', required: true  },
      { name: 'contactPhone', initialValue: value.contactPhone || '', isPhoneNumber: true, required: true  },
      { name: 'email', initialValue: value.email || '', isEmail: true, type: 'email', required: true  },
    ];
    return (<CompanyEditForm formId='editContact' fields={fields} view={view} onContinue={onContinue()} />);
  };

  const renderNoCompany = (continueView) => (
    <div className="noCompanyFound">
      <NeuroMessage id={'lookupFlow.noCompanyFound.header'} tagName='h1' />
      <NeuroMessage id={'lookupFlow.noCompanyFound.text'} tagName='p' />
      <button className="btn" onClick={onAddItem(continueView)}>
        <NeuroMessage id={'lookupFlow.noCompanyFound.addCompany'} />
      </button>
    </div>
  );

  const renderNoContact = (continueView) => (
    <div className="noContactFound">
      <NeuroMessage id={'lookupFlow.noContactFound.header'} tagName='h1' />
      <NeuroMessage id={'lookupFlow.noContactFound.text'} tagName='p' />
      <button className="btn" onClick={onAddItem(continueView)}>
        <NeuroMessage id={'lookupFlow.noContactFound.addContact'} />
      </button>
    </div>
  );

  const renderLoading = () => (
    <div className="lookup-loading"><i class="fa fa-spinner fa-pulse fa-5x"></i></div>
  );

  const renderView = () => {
    if (results.isLoading) {
      return renderLoading();
    }
    const companies = results.companies instanceof Array ? results.companies : [];
    const contacts = results.contacts instanceof Array ? results.contacts : [];

    switch (view.current) {
      case VIEWS.COMPANY_RESULTS:
        if (results.companies?.length === 0) {
          return renderNoCompany(VIEWS.ADD_COMPANY);
        }
        return renderResults(VIEWS.ADD_COMPANY, companies, selections.company, VIEWS.CONTACT_RESULTS);
      case VIEWS.CONTACT_RESULTS:
        if (results.contacts?.length === 0) {
          return renderNoContact(VIEWS.ADD_CONTACT);
        }
        return renderResults(VIEWS.ADD_CONTACT, contacts, selections.contact);
      case VIEWS.ADD_COMPANY:
        return renderAddCompany(VIEWS.ADD_CONTACT);
      case VIEWS.EDIT_COMPANY:
        return renderEditCompany(VIEWS.EDIT_CONTACT);
      case VIEWS.ADD_CONTACT:
        return renderAddContact();
      case VIEWS.EDIT_CONTACT:
        return renderEditContact();
      case VIEWS.NO_COMPANY:
        return renderNoCompany(VIEWS.ADD_COMPANY);
      case VIEWS.NO_CONTACT:
        return renderNoContact(VIEWS.ADD_CONTACT);
    }
  };

  const renderBackBtn = () => !results.isLoading && view.history.length > 0 && (
    <div className="lookup-back">
      <button className="lookup-back__btn" onClick={onBack}><i className="fa fa-chevron-left" /><NeuroMessage id={'lookupFlow.back'} /></button>
    </div>
  );

  return (
    <Modal
      show={isOpen}
      className={classes}
      onClose={() => onClose(view.current)}
      closeBtn={<i className={'fa fa-times'} />}
      closeOnOuterClick={false}
      nodeId="root"
    >
      {renderBackBtn()}
      {isOpen && renderView()}
    </Modal>
  );

};

LookupFlow.propTypes = {
  classes: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onContactSearch: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  results: PropTypes.object,
  type: PropTypes.string,
  value: PropTypes.object,
  valueChanged: PropTypes.func.isRequired,
  accountIdParam: PropTypes.string,
};

export default LookupFlow;